import { useEffect, useState } from 'react';

/**
 * One-time confirmation after following an emailed link.
 *
 * `/api/auth/verify` redirects to `/?activated=1` (first activation, which also
 * signs them in) or `/?verified=1` (re-confirming an already-active account).
 * Without this the user clicks a link in their inbox and simply materialises
 * inside the app with no acknowledgement that anything happened — which reads
 * as "did that work?" rather than "I'm in".
 *
 * The param is stripped once shown, so a refresh, a back-button, or a pasted URL
 * doesn't replay the message.
 *
 * Deliberately silent on /login: that page renders its own banner for the same
 * params (including the failure case), and two notices for one event is worse
 * than none.
 */
export function ActivationToast() {
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (window.location.pathname.startsWith('/login')) return;
    const p = new URLSearchParams(window.location.search);
    const activated = p.get('activated') === '1';
    const verified = p.get('verified') === '1';
    if (!activated && !verified) return;

    setMsg(
      activated
        ? "Account activated — you're signed in."
        : 'Email confirmed.'
    );

    // Drop the params without going through the router: this is a cosmetic URL
    // tidy-up, and a router navigation here would remount the page underneath.
    p.delete('activated');
    p.delete('verified');
    const qs = p.toString();
    window.history.replaceState(
      {},
      '',
      window.location.pathname + (qs ? '?' + qs : '') + window.location.hash
    );

    const t = setTimeout(() => setMsg(null), 6000);
    return () => clearTimeout(t);
  }, []);

  if (!msg) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      onClick={() => setMsg(null)}
      style={{
        position: 'fixed',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '11px 18px',
        borderRadius: 10,
        background: 'var(--panel-2)',
        border: '1px solid var(--success-green)',
        color: 'var(--text)',
        fontSize: 14,
        fontWeight: 500,
        boxShadow: '0 8px 28px rgba(0,0,0,0.35)',
        cursor: 'pointer',
        maxWidth: 'calc(100vw - 32px)',
      }}
    >
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
        <circle cx="12" cy="12" r="10" stroke="var(--success-green)" strokeWidth="2" />
        <path
          d="M8 12.5l2.5 2.5L16 9.5"
          stroke="var(--success-green)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {msg}
    </div>
  );
}
